import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  PersonAddAlt1,
  Home,
} from "@mui/icons-material";
import api from "../services/api";
import toast from "react-hot-toast";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    phone_number: "",
    address: "",
    education: "",
    role: "student",
  });

  // Webcam states
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error("Profile photo must be less than 2MB");
        e.target.value = null;
        return;
      }
      setProfilePhoto(file);
      // agar upload se photo li, to captured image clear
      setCapturedImage(null);
    }
  };

  // ✅ Camera start (simple & stable)
  const startCamera = async () => {
    try {
      console.log("Requesting camera access...");
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error("Camera API not supported in this browser.");
        return;
      }

      setShowCamera(true);
      setCapturedImage(null);
      setCameraReady(false);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // Wait until video metadata is loaded then play
        videoRef.current.onloadedmetadata = async () => {
          try {
            await videoRef.current.play();
            setCameraReady(true);
            console.log("Camera is ready.");
          } catch (playErr) {
            console.error("video.play() error:", playErr);
            toast.error("Unable to start camera preview.");
          }
        };
      }
    } catch (err) {
      console.error("Camera error:", err);
      if (err.name === "NotReadableError") {
        toast.error(
          "Camera already in use. Dusre app/tab (Zoom, Meet, WhatsApp) se camera band karke dubara try karo."
        );
      } else {
        toast.error(
          "Camera access blocked. Permission allow karo ya localhost/https se open karo."
        );
      }
    }
  };

  // Camera stop
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCameraReady(false);
  };

  // Frame capture
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    if (!cameraReady) {
      toast.error("Camera starting hai, 1–2 second baad CAPTURE dabao.");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video.videoWidth || !video.videoHeight) {
      // Safety: agar abhi bhi 0 hai to fixed size use kar lo
      canvas.width = 640;
      canvas.height = 480;
    } else {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg");
    setCapturedImage(dataUrl);
  };

  // base64 → Blob
  const dataURLtoBlob = (dataUrl) => {
    const byteString = atob(dataUrl.split(",")[1]);
    const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const safeFormData = { ...formData, role: "student" };
    const data = new FormData();
    for (let key in safeFormData) {
      data.append(key, safeFormData[key]);
    }

    // PRIORITY: capturedImage (camera) > profilePhoto (upload)
    if (capturedImage) {
      const blob = dataURLtoBlob(capturedImage);
      data.append("profile_photo", blob, "profile_photo.jpg");
    } else if (profilePhoto) {
      data.append("profile_photo", profilePhoto);
    }

    try {
      await api.post("users/register/", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      alert("✅ Registered successfully!");
      navigate("/login");
    } catch (err) {
      if (err.response?.data && typeof err.response.data === "object") {
        const errors = err.response.data;
        let errorMsg = "";
        for (const key in errors) {
          if (Array.isArray(errors[key])) {
            errorMsg += `${key}: ${errors[key].join(", ")}\n`;
          } else {
            errorMsg += `${key}: ${errors[key]}\n`;
          }
        }
        setError(errorMsg);
      } else {
        setError("❌ Registration failed. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-6xl w-full bg-white rounded-xl shadow-lg grid grid-cols-1 md:grid-cols-2 overflow-hidden relative">
        {/* Home Button */}
        <button
          type="button"
          onClick={() => navigate("/")}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white shadow hover:bg-gray-100 transition-colors"
          title="Home"
        >
          <Home fontSize="medium" className="text-blue-600" />
        </button>

        {/* SVG Illustration */}
        <div className="hidden md:flex items-center justify-center bg-blue-50 p-6">
          <img
            src="images/register.svg"
            alt="Register Illustration"
            className="max-w-md"
          />
        </div>

        {/* Register Form */}
        <div className="p-6 pt-14 md:p-10">
          <div className="mb-6 flex items-center gap-2 text-primaryText text-xl font-bold">
            <PersonAddAlt1 className="text-blue-700" />
            <Typography variant="h5">Create Student Account</Typography>
          </div>

          <form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <TextField
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
              <TextField
                label="Middle Name"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleChange}
              />
              <TextField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>

            <TextField
              label="Username"
              name="username"
              fullWidth
              required
              value={formData.username}
              onChange={handleChange}
            />

            <TextField
              label="Email"
              name="email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={handleChange}
            />

            <TextField
              label="Password"
              name="password"
              fullWidth
              required
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Phone Number"
              name="phone_number"
              fullWidth
              required
              value={formData.phone_number}
              onChange={handleChange}
            />

            <TextField
              label="Address"
              name="address"
              fullWidth
              required
              value={formData.address}
              onChange={handleChange}
            />

            <TextField
              label="Education"
              name="education"
              fullWidth
              required
              value={formData.education}
              onChange={handleChange}
            />

            {/* Upload + Capture Section */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Optional: Upload Profile Photo (PNG, JPG, JPEG, Max Size 2MB)
              </label>
              <input
                type="file"
                accept="image/png,image/jpg,image/jpeg"
                onChange={handlePhotoChange}
                className="block w-full text-sm border border-gray-300 rounded px-3 py-2"
              />

              {/* Capture via Camera */}
              <div className="mt-3 flex items-center gap-3">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={startCamera}
                >
                  Capture photo via Camera
                </Button>

                {/* Preview if captured */}
                {capturedImage && (
                  <img
                    src={capturedImage}
                    alt="Captured"
                    className="w-12 h-12 rounded-full object-cover border"
                  />
                )}
              </div>

              <p className="text-xs text-gray-500 mt-1">
                You can either upload a profile photo or capture it using your
                device camera. After registration, profile photo cannot be
                changed.
              </p>
            </div>

            {error && (
              <pre className="text-red-600 whitespace-pre-line text-sm">
                {error}
              </pre>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="bg-blue-700 text-white hover:bg-blue-800"
            >
              Register
            </Button>
          </form>
        </div>
      </div>

      {/* CAMERA MODAL */}
      {showCamera && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-md">
            <Typography variant="h6" className="mb-2">
              Capture Profile Photo
            </Typography>

            <video
              ref={videoRef}
              className="w-full rounded-md bg-black"
              autoPlay
              playsInline
            />

            <canvas ref={canvasRef} className="hidden" />

            {capturedImage && (
              <div className="mt-3 flex flex-col items-center">
                <Typography variant="body2" className="mb-1">
                  Preview:
                </Typography>
                <img
                  src={capturedImage}
                  alt="Captured Preview"
                  className="w-24 h-24 rounded-full object-cover border"
                />
              </div>
            )}

            <div className="flex justify-between mt-4 gap-2">
              <Button
                type="button"
                onClick={handleCapture}
                variant="outlined"
                size="small"
                disabled={!cameraReady}
              >
                Capture
              </Button>
              <Button
                type="button"
                onClick={stopCamera}
                variant="contained"
                size="small"
              >
                Done
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setCapturedImage(null);
                  stopCamera();
                }}
                color="error"
                variant="outlined"
                size="small"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
